/**
 * /login — email/password login against auth.thriv.es.
 *
 * Once authenticated, the user is redirected to `?next` or to their first
 * workspace.
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useAuth } from '@/features/auth/authContext'
import { GoogleSignInButton } from '@/features/auth/GoogleSignInButton'
import { ApiError } from '@/lib/api'
import { FridayMark } from '@/components/branding/FridayMark'
import { useTheme } from '@/features/theme/ThemeProvider'

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/login' }) as { next?: string }
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { theme } = useTheme()

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await login(email, password, remember)
      navigate({ to: search.next ?? '/w' })
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Login failed'
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-app px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <FridayMark size={64} className="mx-auto mb-3" />
          <h1 className="text-2xl font-semibold tracking-tight">Sign in to Friday</h1>
          <p className="mt-1 text-sm text-fg-muted">Project management for teams that ship.</p>
        </div>
        <form
          onSubmit={onSubmit}
          className="space-y-3 border border-border bg-elevated p-6 shadow-lg"
          style={{ borderRadius: 'var(--radius-lg)' }}
        >
          <label className="block">
            <span className="text-xs font-medium text-fg">Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              className="input mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-fg">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength={8}
              className="input mt-1"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </label>
          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-accent hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-fg-muted">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              disabled={busy}
            />
            <span>Remember me on this device</span>
          </label>
          {error && (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
              {error}
            </div>
          )}
          <button
            type="submit"
            className="btn-primary w-full justify-center"
            disabled={busy}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="flex items-center gap-3 text-xs text-fg-muted">
            <span className="h-px flex-1 bg-border" />
            <span>or</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <GoogleSignInButton next={search.next} remember={remember} />
          <div className="flex items-center gap-3 text-xs text-fg-muted">
            <span className="h-px flex-1 bg-border" />
            <span>or</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <Link
            to="/signup"
            search={{ next: search.next }}
            className="btn-outline w-full justify-center"
          >
            Create an account
          </Link>
        </form>
        <p className="mt-4 text-center text-xs text-fg-muted">
          By signing in you agree to our terms of service.
          <br />
          <span className="opacity-60">Theme · {theme.name}</span>
        </p>
      </div>
    </div>
  )
}